import { useMemo, type ComponentType, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import {
  Activity,
  Bot,
  Building2,
  Database,
  FlaskConical,
  LogOut,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { useAppStore } from '../../stores/appStore'
import { useChatStore } from '../../stores/chatStore'
import { auth } from '../../lib/auth'
import { fetchConversations, type ApiConversation } from '../../lib/chatApi'
import { useAuth } from '../auth-context'
import { useActiveWorkspace } from '../../lib/workspace'

const NAV = [
  { to: '/data', label: 'Data Lake', icon: Database },
  { to: '/theses', label: 'Theses', icon: FlaskConical },
  { to: '/agents', label: 'Agents', icon: Bot },
  { to: '/trades', label: 'Trades', icon: Activity },
  { to: '/models', label: 'Models', icon: SlidersHorizontal },
  { to: '/organization', label: 'Organization', icon: Building2, adminOnly: true },
] as const

function formatRelativeTime(dateStr: string): string {
  const then = new Date(dateStr).getTime()
  if (Number.isNaN(then)) return ''
  const diffMin = Math.floor((Date.now() - then) / 60_000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return new Date(dateStr).toLocaleDateString()
}

function activeConversationFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/assistant\/([^/?#]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

function conversationTitle(conv: ApiConversation): string {
  return conv.title?.trim() || 'New Conversation'
}

export function Sidebar() {
  const sidebarOpen = useAppStore((s) => s.sidebarOpen)
  const setSidebarOpen = useAppStore((s) => s.setSidebarOpen)
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed)
  const setSidebarCollapsed = useAppStore((s) => s.setSidebarCollapsed)
  const setActiveConversation = useChatStore((s) => s.setActiveConversation)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const activeConversationId = activeConversationFromPath(pathname)
  const session = useAuth()
  const { activeWorkspaceKey, setActiveWorkspaceKey } = useActiveWorkspace()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: conversations } = useQuery({
    queryKey: ['conversations', activeWorkspaceKey],
    queryFn: () => fetchConversations(activeWorkspaceKey),
    enabled: !!activeWorkspaceKey,
    refetchOnWindowFocus: false,
  })

  const sortedConversations = useMemo(
    () => [...(conversations ?? [])].sort((a, b) => new Date(b.last_active_at).getTime() - new Date(a.last_active_at).getTime()),
    [conversations],
  )

  const displayName = session.user?.firstName
    ? `${session.user.firstName} ${session.user.lastName || ''}`.trim()
    : session.user?.email || 'User'
  const avatarLetter = session.user?.firstName?.[0] || session.user?.email?.[0]?.toUpperCase() || '?'
  const visibleNav = NAV.filter((item) => !('adminOnly' in item) || !item.adminOnly || session.organization?.role === 'admin')

  const isPathActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`)

  const setWorkspace = (key: string) => {
    setActiveWorkspaceKey(key)
    setActiveConversation(null)
    void queryClient.invalidateQueries({ queryKey: ['conversations'] })
    if (pathname.startsWith('/assistant')) {
      void navigate({ to: '/assistant' })
    }
  }

  const openAssistantRoot = () => {
    setActiveConversation(null)
    void queryClient.invalidateQueries({ queryKey: ['conversations', activeWorkspaceKey] })
    void navigate({ to: '/assistant' })
    setSidebarOpen(false)
  }

  const openConversation = (id: string) => {
    setActiveConversation(id)
    void navigate({ to: '/assistant/$conversationId', params: { conversationId: id } })
    setSidebarOpen(false)
  }

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {sidebarCollapsed && (
        <button
          type="button"
          className="fixed left-3 top-3 z-30 hidden h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100/80 hover:text-gray-700 md:flex"
          onClick={() => setSidebarCollapsed(false)}
          aria-label="Show sidebar"
        >
          <PanelLeftOpen size={18} />
        </button>
      )}
      <div
        className={`fixed inset-y-0 left-0 z-40 flex h-full w-64 flex-col border-r border-border-default/50 bg-white shadow-2xl transition-transform duration-200 md:static md:shrink-0 md:translate-x-0 md:bg-sidebar-bg md:shadow-none md:pointer-events-auto ${
          sidebarOpen ? 'translate-x-0' : 'pointer-events-none -translate-x-full md:translate-x-0'
        } ${sidebarCollapsed ? 'md:hidden' : ''}`}
      >
        <div className="flex items-center gap-2 border-b border-border-default/50 p-3">
          <button
            onClick={openAssistantRoot}
            data-testid="new-chat-button"
            className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <Plus size={16} />
            New Chat
          </button>
          <SidebarIconButton className="md:hidden" onClick={() => setSidebarOpen(false)} label="Close sidebar">
            <X size={18} />
          </SidebarIconButton>
          <SidebarIconButton className="hidden md:flex" onClick={() => setSidebarCollapsed(true)} label="Collapse sidebar">
            <PanelLeftClose size={16} />
          </SidebarIconButton>
        </div>

        <div className="border-b border-border-default/50 px-3 py-3">
          <select
            value={activeWorkspaceKey}
            onChange={(e) => setWorkspace(e.target.value)}
            disabled={session.workspaces.length === 0}
            className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 outline-none focus:border-slate-400 disabled:text-gray-400"
            aria-label="Active workspace"
          >
            {session.workspaces.length === 0 ? (
              <option value="">No workspace</option>
            ) : (
              session.workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.key}>
                  {workspace.name}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sortedConversations.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-text-muted">No conversations yet</div>
          )}
          {sortedConversations.map((conv) => {
            const active = conv.id === activeConversationId
            return (
              <button
                key={conv.id}
                type="button"
                onClick={() => openConversation(conv.id)}
                className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors ${
                  active ? 'bg-accent text-accent-text' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <MessageSquare size={16} className="mt-0.5 shrink-0 opacity-50" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{conversationTitle(conv)}</span>
                  <span className="mt-0.5 block text-xs text-text-muted">{formatRelativeTime(conv.last_active_at)}</span>
                </span>
              </button>
            )
          })}
        </div>

        <div className="border-t border-border-default/50">
          {visibleNav.map(({ to, label, icon }) => (
            <NavButton
              key={to}
              active={isPathActive(to)}
              onClick={() => {
                void navigate({ to, search: to === '/trades' ? {} : undefined })
                setSidebarOpen(false)
              }}
              icon={icon}
              label={label}
              testId={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            />
          ))}
          <UserFooter
            displayName={displayName}
            avatarLetter={avatarLetter}
            organizationName={session.organization?.name ?? 'No organization'}
            onOpenProfile={() => {
              void navigate({ to: '/profile' })
              setSidebarOpen(false)
            }}
          />
        </div>
      </div>
    </>
  )
}

function SidebarIconButton({
  className,
  onClick,
  label,
  children,
}: {
  className?: string
  onClick: () => void
  label: string
  children: ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 ${className ?? ''}`}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  )
}

function NavButton({
  active,
  onClick,
  icon: Icon,
  label,
  testId,
}: {
  active: boolean
  onClick: () => void
  icon: ComponentType<{ size?: number; className?: string }>
  label: string
  testId: string
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors ${
        active ? 'bg-accent text-accent-text' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon size={16} className="shrink-0 opacity-60" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  )
}

function UserFooter({
  displayName,
  avatarLetter,
  organizationName,
  onOpenProfile,
}: {
  displayName: string
  avatarLetter: string
  organizationName: string
  onOpenProfile: () => void
}) {
  return (
    <div className="border-t border-border-default/50">
      <div className="flex items-center gap-1 px-3 py-2">
        <button
          type="button"
          onClick={onOpenProfile}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1 py-1 text-left transition-colors hover:bg-gray-100"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-xs font-medium text-white">
            {avatarLetter}
          </div>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-gray-700">{displayName}</span>
            <span className="block truncate text-xs text-text-muted">{organizationName}</span>
          </span>
        </button>
        <button
          onClick={() => void auth.signOut()}
          className="shrink-0 rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut size={14} />
        </button>
      </div>
    </div>
  )
}
